import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  getSystemState,
  startSystem,
  stopSystem,
  restartConnectors,
  getSystemJob,
  SystemState,
  SystemJob,
  Subsystem
} from 'src/Api/exchanges';

interface UpdatesContextProps {
  // System state
  systemState?: SystemState;
  isSystemRunning: boolean;
  isOrderbooksRunning: boolean;
  isPredictionsRunning: boolean;
  isLoading: boolean;

  // Job tracking
  currentJob?: SystemJob | null;
  isJobRunning: boolean;

  // Legacy compatibility
  updatesStatus?: boolean;
  toggleUpdates: (restart?: boolean) => Promise<void>;

  // Actions
  toggleSystem: (subsystem?: Subsystem) => Promise<void>;
  startSystem: (subsystem?: Subsystem) => Promise<void>;
  stopSystem: (subsystem?: Subsystem) => Promise<void>;
  restartConnectors: (exchanges?: string[]) => Promise<void>;

  // Loading states
  isToggleLoading: boolean;
  isRestartLoading: boolean;
}

const UpdatesContext = createContext<UpdatesContextProps | undefined>(
  undefined
);

const SYSTEM_STATE_QUERY_KEY = 'system/state';
const SYSTEM_JOB_QUERY_KEY = 'system/job';

export const UpdatesProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const queryClient = useQueryClient();
  const pollingRef = useRef(false);

  // Query system state
  const { data: systemState, isLoading } = useQuery(
    SYSTEM_STATE_QUERY_KEY,
    getSystemState,
    {
      refetchInterval: 30000,
      retry: 1,
      onError: error => {
        console.error('Failed to fetch system state:', error);
      }
    }
  );

  // Query job status — only polls when a job is running
  const { data: jobData } = useQuery(SYSTEM_JOB_QUERY_KEY, getSystemJob, {
    refetchInterval: pollingRef.current ? 2000 : false,
    enabled: pollingRef.current,
    retry: 1
  });

  // Determine if a job is currently running
  const isJobRunning = jobData?.status === 'running';

  // Start/stop polling when job state changes
  useEffect(() => {
    if (isJobRunning && !pollingRef.current) {
      pollingRef.current = true;
      queryClient.invalidateQueries(SYSTEM_JOB_QUERY_KEY);
    } else if (!isJobRunning && pollingRef.current) {
      pollingRef.current = false;
      // Job finished — refresh system state
      queryClient.invalidateQueries(SYSTEM_STATE_QUERY_KEY);
    }
  }, [isJobRunning, queryClient]);

  // Start system mutation
  const startMutation = useMutation(
    (subsystem?: Subsystem) => startSystem(subsystem),
    {
      onSuccess: (job: SystemJob) => {
        // Seed the cache immediately so the stepper shows before the first poll
        queryClient.setQueryData(SYSTEM_JOB_QUERY_KEY, job);
        pollingRef.current = true;
        queryClient.invalidateQueries(SYSTEM_JOB_QUERY_KEY);
      }
    }
  );

  // Stop system mutation
  const stopMutation = useMutation(
    (subsystem?: Subsystem) => stopSystem(subsystem),
    {
      onSuccess: (job: SystemJob) => {
        queryClient.setQueryData(SYSTEM_JOB_QUERY_KEY, job);
        pollingRef.current = true;
        queryClient.invalidateQueries(SYSTEM_JOB_QUERY_KEY);
      }
    }
  );

  // Restart connectors mutation
  const restartMutation = useMutation(
    (exchanges?: string[]) => restartConnectors(exchanges),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(SYSTEM_STATE_QUERY_KEY);
      }
    }
  );

  // In development, always assume system is running (no AWS scaling)
  const isDev = import.meta.env.DEV;
  const isSystemRunning = isDev ? true : (systemState?.isRunning ?? false);
  const isOrderbooksRunning = isDev
    ? true
    : (systemState?.subsystems?.orderbooks ?? false);
  const isPredictionsRunning = isDev
    ? true
    : (systemState?.subsystems?.predictions ?? false);

  const toggleSystem = useCallback(
    async (subsystem?: Subsystem) => {
      if (isJobRunning) return; // Prevent double-trigger

      if (!subsystem || subsystem === 'all') {
        // Toggle entire system
        if (isSystemRunning) {
          await stopMutation.mutateAsync(undefined);
        } else {
          await startMutation.mutateAsync(undefined);
        }
      } else if (subsystem === 'orderbooks') {
        if (isOrderbooksRunning) {
          await stopMutation.mutateAsync('orderbooks');
        } else {
          await startMutation.mutateAsync('orderbooks');
        }
      } else if (subsystem === 'predictions') {
        if (isPredictionsRunning) {
          await stopMutation.mutateAsync('predictions');
        } else {
          await startMutation.mutateAsync('predictions');
        }
      }
    },
    [
      isSystemRunning,
      isOrderbooksRunning,
      isPredictionsRunning,
      isJobRunning,
      startMutation,
      stopMutation
    ]
  );

  // Legacy toggleUpdates for backward compatibility
  const toggleUpdates = useCallback(
    async (restart?: boolean) => {
      if (restart) {
        await restartMutation.mutateAsync(undefined);
      } else {
        await toggleSystem();
      }
    },
    [toggleSystem, restartMutation]
  );

  return (
    <UpdatesContext.Provider
      value={{
        systemState,
        isSystemRunning,
        isOrderbooksRunning,
        isPredictionsRunning,
        isLoading,
        // Job tracking
        currentJob: jobData,
        isJobRunning,
        // Legacy compatibility
        updatesStatus: isSystemRunning,
        toggleUpdates,
        // New API
        toggleSystem,
        startSystem: async (subsystem?: Subsystem) => {
          await startMutation.mutateAsync(subsystem);
        },
        stopSystem: async (subsystem?: Subsystem) => {
          await stopMutation.mutateAsync(subsystem);
        },
        restartConnectors: async exchanges => {
          await restartMutation.mutateAsync(exchanges);
        },
        isToggleLoading: startMutation.isLoading || stopMutation.isLoading,
        isRestartLoading: restartMutation.isLoading
      }}
    >
      {children}
    </UpdatesContext.Provider>
  );
};

export const useUpdates = () => {
  const context = useContext(UpdatesContext);
  if (!context) {
    throw new Error('useUpdates must be used within an UpdatesProvider');
  }
  return context;
};

// Legacy compatibility - map to new API
export const useSystemControl = useUpdates;
